/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AppLanguage, AppScreen, ChildProfile } from './types';
import { useLanguage } from './context/LanguageContext';
import Mascot from './components/Mascot';
import SplashAndProfile from './components/SplashAndProfile';
import ErrorBoundary from './components/ErrorBoundary';

import { lazyWithPrefetch, prefetchCommonAssets } from './hoc/withPrefetch';

const Dashboard = lazyWithPrefetch(() => import('./components/Dashboard'), 'Dashboard');

const AlphabetModule = lazyWithPrefetch(() => import('./components/AlphabetModule'), 'AlphabetModule');
const WordsModule = lazyWithPrefetch(() => import('./components/WordsModule'), 'WordsModule');
const NumbersModule = lazyWithPrefetch(() => import('./components/NumbersModule'), 'NumbersModule');
const ActivitiesModule = lazyWithPrefetch(() => import('./components/ActivitiesModule'), 'ActivitiesModule');
const RewardsModule = lazyWithPrefetch(() => import('./components/RewardsModule'), 'RewardsModule');
const VoicePracticeModule = lazyWithPrefetch(() => import('./components/VoicePracticeModule'), 'VoicePracticeModule');
const ParentArea = lazyWithPrefetch(() => import('./components/ParentArea'), 'ParentArea');
const WeatherModule = lazyWithPrefetch(() => import('./components/WeatherModule'), 'WeatherModule');
const MonthsModule = lazyWithPrefetch(() => import('./components/MonthsModule'), 'MonthsModule');
const DaysModule = lazyWithPrefetch(() => import('./components/DaysModule'), 'DaysModule');
const BodyPartsModule = lazyWithPrefetch(() => import('./components/BodyPartsModule'), 'BodyPartsModule');
const IslamicMonthsModule = lazyWithPrefetch(() => import('./components/IslamicMonthsModule'), 'IslamicMonthsModule');
const CalendarLearningModule = lazyWithPrefetch(() => import('./components/CalendarLearningModule'), 'CalendarLearningModule');
const RamadanModeModule = lazyWithPrefetch(() => import('./components/RamadanModeModule'), 'RamadanModeModule');
const WriteLearnModule = lazyWithPrefetch(() => import('./components/WriteLearnModule'), 'WriteLearnModule');
const BasicCommunicationModule = lazyWithPrefetch(() => import('./components/BasicCommunicationModule'), 'BasicCommunicationModule');
const QuizModule = lazyWithPrefetch(() => import('./components/QuizModule'), 'QuizModule');
import { playSynthSound, speakText } from './utils/speaker';
import { Home, BookOpen, Gamepad2, Award, ShieldCheck, Volume2, VolumeX, Plus, Wifi, WifiOff } from 'lucide-react';
const SeoContent = lazyWithPrefetch(() => import('./components/SeoContent'), 'SeoContent');

const LOCAL_PROFILES_KEY = 'llw_profiles_v1';
const LOCAL_ACTIVE_ID_KEY = 'llw_active_id_v1';
const LOCAL_LANG_KEY = 'llw_lang_v1';

export const getPastDateStr = (daysAgo: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split('T')[0];
};

const DEFAULT_PROFILE: ChildProfile = {
  id: 'explorer_default',
  name: 'Little Explorer',
  avatar: '🐱',
  bgColor: 'bg-skyblue',
  stars: 0,
  level: 1,
  unlockedBadges: [],
  unlockedStickers: [],
  dailyGoalMinutes: 10,
  todayMinutes: 0,
  soundEnabled: true,
  progress: {
    enAlphabets: [],
    urAlphabets: [],
    arAlphabets: [],
    words: [],
    numbers: [],
    activitiesCount: 0,
    weather: [],
    months: [],
    days: [],
    bodyParts: [],
    islamicMonths: [],
    calendarLearning: [],
    ramadanMode: [],
    writeLearn: [],
    communication: [],
    quizHistory: []
  },
  activeDays: [],
  learningHistory: [
    { date: getPastDateStr(6), minutes: 0 },
    { date: getPastDateStr(5), minutes: 0 },
    { date: getPastDateStr(4), minutes: 0 },
    { date: getPastDateStr(3), minutes: 0 },
    { date: getPastDateStr(2), minutes: 0 },
    { date: getPastDateStr(1), minutes: 0 },
    { date: getPastDateStr(0), minutes: 0 }
  ]
};

export default function App() {
  const { currentLanguage, setLanguage, isRtl, t, isLoadingData, loadLanguageData } = useLanguage();
  const [isBooting, setIsBooting] = useState<boolean>(true);
  const [profiles, setProfiles] = useState<ChildProfile[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string>('');
  const [activeScreen, setActiveScreen] = useState<AppScreen>('splash');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  // Sync & persistence states
  const [syncStatus, setSyncStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  // Online connection and local storage status info
  const [isOnline, setIsOnline] = useState<boolean>(typeof window !== 'undefined' ? window.navigator.onLine : true);
  const [showConnectionInfo, setShowConnectionInfo] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Prefetch common components and alternative translation files once the dashboard is active
  useEffect(() => {
    if (activeScreen === 'dashboard') {
      prefetchCommonAssets(loadLanguageData, currentLanguage);
    }
  }, [activeScreen, loadLanguageData, currentLanguage]);

  // Unified Initialization on Mount
  useEffect(() => {
    console.log("[App Initialization] Starting boot sequence...");
    
    // Safety timeout: Guarantee that isBooting is set to false even if storage operations take unexpectedly long
    const safetyBootTimer = setTimeout(() => {
      console.warn("[App Initialization] Boot timeout safety triggered after 1500ms. Force setting isBooting to false.");
      setIsBooting(false);
    }, 1500);

    try {
      const cachedProfiles = localStorage.getItem(LOCAL_PROFILES_KEY);
      const cachedActiveId = localStorage.getItem(LOCAL_ACTIVE_ID_KEY);
      const cachedLastSaved = localStorage.getItem('llw_last_saved');
      const savedScreen = localStorage.getItem('llw_active_screen') as AppScreen | null;

      console.log("[App Initialization] Read storage values:", {
        hasCachedProfiles: !!cachedProfiles,
        cachedActiveId,
        cachedLastSaved,
        savedScreen
      });

      if (cachedLastSaved) {
        setLastSaved(cachedLastSaved);
      } else {
        setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      }

      let loadedProfiles: ChildProfile[] = [];

      if (cachedProfiles) {
        try {
          let parsed: ChildProfile[] = JSON.parse(cachedProfiles);
          if (Array.isArray(parsed) && parsed.length > 0) {
            let updatedCache = false;
            parsed = parsed.map((p) => {
              // Clean legacy pre-filled template items
              const hasLegacyItems = 
                p.progress?.enAlphabets?.includes('A') ||
                p.progress?.urAlphabets?.includes('ا') ||
                p.progress?.arAlphabets?.includes('أ') ||
                p.progress?.words?.includes('1') ||
                p.progress?.numbers?.includes(1);

              if (hasLegacyItems || (p.id === 'explorer_default' && p.stars === 20)) {
                updatedCache = true;
                return {
                  ...p,
                  stars: p.stars === 20 ? 0 : p.stars,
                  unlockedBadges: p.unlockedBadges?.filter((b) => b !== 'first_steps') || [],
                  unlockedStickers: p.unlockedStickers?.filter((s) => s !== 'st1') || [],
                  progress: {
                    ...p.progress,
                    enAlphabets: p.progress?.enAlphabets?.filter((x) => x !== 'A' && x !== 'B') || [],
                    urAlphabets: p.progress?.urAlphabets?.filter((x) => x !== 'ا') || [],
                    arAlphabets: p.progress?.arAlphabets?.filter((x) => x !== 'أ') || [],
                    words: p.progress?.words?.filter((x) => x !== '1' && x !== '4') || [],
                    numbers: p.progress?.numbers?.filter((x) => x !== 1 && x !== 2 && x !== 3) || []
                  }
                };
              }
              return p;
            });
            loadedProfiles = parsed;
            setProfiles(parsed);
            if (updatedCache) {
              localStorage.setItem(LOCAL_PROFILES_KEY, JSON.stringify(parsed));
            }
          }
        } catch (err) {
          console.error("[App Initialization] Error parsing cached profiles:", err);
        }
      }

      if (loadedProfiles.length === 0) {
        console.log("[App Initialization] No valid cached profiles. Creating starter profile.");
        loadedProfiles = [DEFAULT_PROFILE];
        setProfiles(loadedProfiles);
        localStorage.setItem(LOCAL_PROFILES_KEY, JSON.stringify(loadedProfiles));
        localStorage.setItem(LOCAL_ACTIVE_ID_KEY, loadedProfiles[0].id);
      }

      if (cachedActiveId && loadedProfiles.some((p) => p.id === cachedActiveId)) {
        setActiveProfileId(cachedActiveId);
        console.log("[App Initialization] Selected active profile:", cachedActiveId);
      } else {
        setActiveProfileId(loadedProfiles[0].id);
        localStorage.setItem(LOCAL_ACTIVE_ID_KEY, loadedProfiles[0].id);
        console.log("[App Initialization] Fallback active profile:", loadedProfiles[0].id);
      }

      const targetScreen = savedScreen || 'splash';
      setActiveScreen(targetScreen);
      console.log("[App Initialization] Target screen initialized:", targetScreen);

    } catch (err) {
      console.error("[App Initialization] Unexpected error during boot initialization:", err);
    } finally {
      clearTimeout(safetyBootTimer);
      setIsBooting(false);
      console.log("[App Initialization] Boot sequence completed.");
    }
  }, []);

  const activeProfile = useMemo(() => {
    return profiles.find((p) => p.id === activeProfileId) || profiles[0] || DEFAULT_PROFILE;
  }, [profiles, activeProfileId]);

  const handleGlobalLanguageSelect = async (lang: AppLanguage) => {
    setShowLanguageModal(false);
    playSynthSound('badge');
    try {
      await loadLanguageData(lang);
    } catch (e) {
      console.error(e);
    }
    setLanguage(lang);
  };

  // Sound toggler
  const handleToggleSound = (enabled: boolean) => {
    setSoundEnabled(enabled);
    if (activeProfile) {
      const updated = profiles.map((p) =>
        p.id === activeProfile.id ? { ...p, soundEnabled: enabled } : p
      );
      saveProfilesToDb(updated);
    }
  };

  // Core database persistence sync
  const saveProfilesToDb = (updatedProfiles: ChildProfile[]) => {
    setProfiles(updatedProfiles);
    setSyncStatus('saving');
    
    try {
      localStorage.setItem(LOCAL_PROFILES_KEY, JSON.stringify(updatedProfiles));
      
      const now = new Date();
      const timestamp = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      localStorage.setItem('llw_last_saved', timestamp);
      
      // Simulate database persistence delay for visual indicator
      setTimeout(() => {
        setLastSaved(timestamp);
        setSyncStatus('saved');
      }, 500);
    } catch (err) {
      console.error("Local storage persistent sync failed", err);
      setSyncStatus('error');
    }
  };

  const handleImportProfiles = (importedProfiles: ChildProfile[]) => {
    setProfiles(importedProfiles);
    if (importedProfiles.length > 0) {
      const existingActive = importedProfiles.find(p => p.id === activeProfileId);
      if (existingActive) {
        setActiveProfileId(existingActive.id);
        localStorage.setItem(LOCAL_ACTIVE_ID_KEY, existingActive.id);
      } else {
        setActiveProfileId(importedProfiles[0].id);
        localStorage.setItem(LOCAL_ACTIVE_ID_KEY, importedProfiles[0].id);
      }
    }
    saveProfilesToDb(importedProfiles);
  };

  // Navigated screen change speech feedback
  const handleNavigate = (screen: AppScreen) => {
    setActiveScreen(screen);
    try {
      localStorage.setItem('llw_active_screen', screen);
    } catch (e) {
      console.warn("Unable to save active screen to localStorage", e);
    }
    playSynthSound('click');
  };

  // Prefetch dynamic components on-demand when user hovers/focuses on dashboard cards
  const handlePrefetchScreen = (screen: AppScreen) => {
    try {
      if (screen.startsWith('alphabets-')) {
        AlphabetModule.preload().catch(() => {});
      } else if (screen === 'words') {
        WordsModule.preload().catch(() => {});
      } else if (screen === 'numbers') {
        NumbersModule.preload().catch(() => {});
      } else if (screen === 'activities') {
        ActivitiesModule.preload().catch(() => {});
      } else if (screen === 'rewards') {
        RewardsModule.preload().catch(() => {});
      } else if (screen === 'voice-practice') {
        VoicePracticeModule.preload().catch(() => {});
      } else if (screen === 'parent') {
        ParentArea.preload().catch(() => {});
      } else if (screen === 'weather') {
        WeatherModule.preload().catch(() => {});
      } else if (screen === 'months') {
        MonthsModule.preload().catch(() => {});
      } else if (screen === 'days') {
        DaysModule.preload().catch(() => {});
      } else if (screen === 'body-parts') {
        BodyPartsModule.preload().catch(() => {});
      } else if (screen === 'islamic-months') {
        IslamicMonthsModule.preload().catch(() => {});
      } else if (screen === 'calendar-learning') {
        CalendarLearningModule.preload().catch(() => {});
      } else if (screen === 'ramadan-mode') {
        RamadanModeModule.preload().catch(() => {});
      } else if (screen === 'write-learn') {
        WriteLearnModule.preload().catch(() => {});
      } else if (screen === 'basic-communication') {
        BasicCommunicationModule.preload().catch(() => {});
      } else if (screen === 'quiz') {
        QuizModule.preload().catch(() => {});
      }
    } catch {
      // Ignore background preload errors
    }
  };

  // Create profile callback
  const handleCreateProfile = (name: string, avatar: string, color: string) => {
    const newProfile: ChildProfile = {
      id: 'profile_' + Date.now(),
      name,
      avatar,
      bgColor: color,
      stars: 0,
      level: 1,
      unlockedBadges: [],
      unlockedStickers: [],
      dailyGoalMinutes: 10,
      todayMinutes: 0,
      soundEnabled: true,
      progress: {
        enAlphabets: [],
        urAlphabets: [],
        arAlphabets: [],
        words: [],
        numbers: [],
        activitiesCount: 0,
        weather: [],
        months: [],
        days: [],
        bodyParts: [],
        islamicMonths: [],
        calendarLearning: [],
        ramadanMode: [],
        writeLearn: [],
        quizHistory: []
      },
      activeDays: [],
      learningHistory: [
        { date: getPastDateStr(6), minutes: 0 },
        { date: getPastDateStr(5), minutes: 0 },
        { date: getPastDateStr(4), minutes: 0 },
        { date: getPastDateStr(3), minutes: 0 },
        { date: getPastDateStr(2), minutes: 0 },
        { date: getPastDateStr(1), minutes: 0 },
        { date: getPastDateStr(0), minutes: 0 }
      ]
    };

    const updated = [...profiles, newProfile];
    saveProfilesToDb(updated);
    setActiveProfileId(newProfile.id);
    localStorage.setItem(LOCAL_ACTIVE_ID_KEY, newProfile.id);
    setActiveScreen('dashboard');
  };

  // Progress reward callback (Alphabets module)
  const handleAwardAlphabetStars = (count: number, letter: string, category: 'en' | 'ur' | 'ar') => {
    if (!activeProfile) return;

    const currentProgress = { ...activeProfile.progress };
    if (category === 'en') {
      if (!currentProgress.enAlphabets.includes(letter)) {
        currentProgress.enAlphabets.push(letter);
      }
    } else if (category === 'ur') {
      if (!currentProgress.urAlphabets.includes(letter)) {
        currentProgress.urAlphabets.push(letter);
      }
    } else if (category === 'ar') {
      if (!currentProgress.arAlphabets.includes(letter)) {
        currentProgress.arAlphabets.push(letter);
      }
    }

    const totalLearnedCount = currentProgress.enAlphabets.length + currentProgress.urAlphabets.length + currentProgress.arAlphabets.length;
    const newBadges = [...activeProfile.unlockedBadges];
    if (totalLearnedCount >= 5 && !newBadges.includes('abc_explorer')) {
      newBadges.push('abc_explorer');
    }

    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1; // Level up every 40 stars!

    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          unlockedBadges: newBadges,
          progress: currentProgress
        };
      }
      return p;
    });

    saveProfilesToDb(updated);
  };

  // Progress reward callback (Words module)
  const handleAwardWordStars = (count: number, wordId: string) => {
    if (!activeProfile) return;

    const currentProgress = { ...activeProfile.progress };
    if (!currentProgress.words.includes(wordId)) {
      currentProgress.words.push(wordId);
    }

    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;

    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });

    saveProfilesToDb(updated);
  };

  // Progress reward callback (Numbers module)
  const handleAwardNumberStars = (count: number, num: number) => {
    if (!activeProfile) return;

    const currentProgress = { ...activeProfile.progress };
    if (!currentProgress.numbers.includes(num)) {
      currentProgress.numbers.push(num);
    }

    const newBadges = [...activeProfile.unlockedBadges];
    if (currentProgress.numbers.length >= 10 && !newBadges.includes('number_star')) {
      newBadges.push('number_star');
    }

    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;

    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          unlockedBadges: newBadges,
          progress: currentProgress
        };
      }
      return p;
    });

    saveProfilesToDb(updated);
  };

  // Progress reward callback (Games module)
  const handleAwardGameStars = (count: number) => {
    if (!activeProfile) return;

    const currentProgress = { ...activeProfile.progress };
    currentProgress.activitiesCount += 1;

    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;

    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });

    saveProfilesToDb(updated);
  };

  const handleAwardVoiceStars = (count: number) => {
    if (!activeProfile) return;
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl)
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleAwardWeatherStars = (count: number, itemId: string) => {
    if (!activeProfile) return;
    const currentProgress = { ...activeProfile.progress };
    const list = currentProgress.weather || [];
    if (!list.includes(itemId)) {
      currentProgress.weather = [...list, itemId];
    }
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleAwardMonthsStars = (count: number, itemId: string) => {
    if (!activeProfile) return;
    const currentProgress = { ...activeProfile.progress };
    const list = currentProgress.months || [];
    if (!list.includes(itemId)) {
      currentProgress.months = [...list, itemId];
    }
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleQuizComplete = (
    category: string,
    ageGroup: '3-6' | '6-8',
    totalQuestions: number,
    correctAnswers: number,
    starsEarned: number
  ) => {
    if (!activeProfile) return;

    const currentProgress = { ...activeProfile.progress };
    const quizHistory = currentProgress.quizHistory ? [...currentProgress.quizHistory] : [];
    
    quizHistory.push({
      category,
      ageGroup,
      totalQuestions,
      correctAnswers,
      timestamp: new Date().toISOString(),
      starsEarned
    });

    currentProgress.quizHistory = quizHistory;

    const newStars = activeProfile.stars + starsEarned;
    const newLvl = Math.floor(newStars / 40) + 1;

    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });

    saveProfilesToDb(updated);
  };

  const handleAwardDaysStars = (count: number, itemId: string) => {
    if (!activeProfile) return;
    const currentProgress = { ...activeProfile.progress };
    const list = currentProgress.days || [];
    if (!list.includes(itemId)) {
      currentProgress.days = [...list, itemId];
    }
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleAwardBodyPartsStars = (count: number, itemId: string) => {
    if (!activeProfile) return;
    const currentProgress = { ...activeProfile.progress };
    const list = currentProgress.bodyParts || [];
    if (!list.includes(itemId)) {
      currentProgress.bodyParts = [...list, itemId];
    }
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleAwardIslamicMonthsStars = (count: number, itemId: string) => {
    if (!activeProfile) return;
    const currentProgress = { ...activeProfile.progress };
    const list = currentProgress.islamicMonths || [];
    if (!list.includes(itemId)) {
      currentProgress.islamicMonths = [...list, itemId];
    }
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleAwardCalendarLearningStars = (count: number, itemId: string) => {
    if (!activeProfile) return;
    const currentProgress = { ...activeProfile.progress };
    const list = currentProgress.calendarLearning || [];
    if (!list.includes(itemId)) {
      currentProgress.calendarLearning = [...list, itemId];
    }
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleAwardRamadanModeStars = (count: number, itemId: string) => {
    if (!activeProfile) return;
    const currentProgress = { ...activeProfile.progress };
    const list = currentProgress.ramadanMode || [];
    if (!list.includes(itemId)) {
      currentProgress.ramadanMode = [...list, itemId];
    }
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleAwardWriteLearnStars = (count: number, itemId: string) => {
    if (!activeProfile) return;
    const currentProgress = { ...activeProfile.progress };
    const list = currentProgress.writeLearn || [];
    if (!list.includes(itemId)) {
      currentProgress.writeLearn = [...list, itemId];
    }
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleAwardCommunicationStars = (count: number, itemId: string) => {
    if (!activeProfile) return;
    const currentProgress = { ...activeProfile.progress };
    const list = currentProgress.communication || [];
    if (!list.includes(itemId)) {
      currentProgress.communication = [...list, itemId];
    }
    const newStars = activeProfile.stars + count;
    const newLvl = Math.floor(newStars / 40) + 1;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: newStars,
          level: Math.max(p.level, newLvl),
          progress: currentProgress
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleUpdateAvatarCustom = (avatarCustom: { expression: string; color: string; accessory: string }) => {
    if (!activeProfile) return;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          avatarCustom
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleUpdateProfileInfo = (name: string, photo: string | undefined) => {
    if (!activeProfile) return;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          name,
          photo
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  // Badge/Sticker Shop Claim triggers
  const handleClaimBadge = (badgeId: string, cost: number) => {
    if (!activeProfile) return;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: p.stars - cost,
          unlockedBadges: [...p.unlockedBadges, badgeId]
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleClaimSticker = (stickerId: string, cost: number) => {
    if (!activeProfile) return;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: p.stars - cost,
          unlockedStickers: [...p.unlockedStickers, stickerId]
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  // Parent settings panel overrides
  const handleResetProgress = () => {
    if (!activeProfile) return;
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        return {
          ...p,
          stars: 0,
          level: 1,
          unlockedBadges: [],
          unlockedStickers: [],
          todayMinutes: 0,
          progress: {
            enAlphabets: [],
            urAlphabets: [],
            arAlphabets: [],
            words: [],
            numbers: [],
            activitiesCount: 0,
            weather: [],
            months: [],
            days: [],
            bodyParts: [],
            islamicMonths: [],
            calendarLearning: [],
            ramadanMode: [],
            writeLearn: []
          }
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  const handleUpdateDailyGoal = (mins: number) => {
    if (!activeProfile) return;
    const updated = profiles.map((p) =>
      p.id === activeProfile.id ? { ...p, dailyGoalMinutes: mins } : p
    );
    saveProfilesToDb(updated);
  };

  const handleSwitchProfile = (profileId: string) => {
    setActiveProfileId(profileId);
    localStorage.setItem(LOCAL_ACTIVE_ID_KEY, profileId);
    setActiveScreen('dashboard');
  };

  // Playtime simulation hook (+1 minute for kids testing!)
  const simulateActiveMinute = () => {
    if (!activeProfile) return;
    playSynthSound('pop');
    const updated = profiles.map((p) => {
      if (p.id === activeProfile.id) {
        const nextMin = p.todayMinutes + 1;
        // If daily minutes meet target, unlock high achiever owl badge!
        const badges = [...p.unlockedBadges];
        if (nextMin >= p.dailyGoalMinutes && !badges.includes('bilingual_owl')) {
          badges.push('bilingual_owl');
        }

        const todayStr = getPastDateStr(0);
        const activeDaysList = p.activeDays || [];
        const nextActiveDays = activeDaysList.includes(todayStr) 
          ? activeDaysList 
          : [...activeDaysList, todayStr];

        let historyList = p.learningHistory || [];
        const existingTodayIdx = historyList.findIndex(h => h.date === todayStr);
        let nextHistory = [...historyList];
        if (existingTodayIdx !== -1) {
          nextHistory[existingTodayIdx] = { date: todayStr, minutes: nextMin };
        } else {
          nextHistory.push({ date: todayStr, minutes: nextMin });
        }

        return {
          ...p,
          todayMinutes: nextMin,
          unlockedBadges: badges,
          activeDays: nextActiveDays,
          learningHistory: nextHistory
        };
      }
      return p;
    });
    saveProfilesToDb(updated);
  };

  if (isBooting) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-6 select-none relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-[10%] left-[5%] w-16 h-16 bg-skyblue/10 rounded-full blur-xl animate-pulse" />
        <div className="absolute bottom-[20%] right-[5%] w-24 h-24 bg-coralpink/15 rounded-full blur-xl animate-pulse" />
        
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="flex flex-col items-center text-center gap-6 z-10"
        >
          {/* Logo animation */}
          <motion.div
            animate={{ y: [0, -15, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="w-32 h-32 bg-white rounded-[2.5rem] shadow-[0_12px_24px_rgba(38,50,56,0.08)] border-4 border-amber-300 flex items-center justify-center text-6xl"
          >
            🚀
          </motion.div>
          
          <div className="space-y-3">
            <h1 className="text-5xl font-black text-navy tracking-tight leading-none bg-gradient-to-r from-coralpink via-orangeaccent to-skyblue bg-clip-text text-transparent">
              Funiqra
            </h1>
            <p className="text-xs font-black text-slate-700 tracking-widest uppercase">
              Little Learning World
            </p>
          </div>

          {/* Simple dot loader */}
          <div className="flex gap-2.5 mt-4">
            <span className="w-3.5 h-3.5 bg-coralpink rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-3.5 h-3.5 bg-orangeaccent rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-3.5 h-3.5 bg-skyblue rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </motion.div>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-6 select-none relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-[10%] left-[5%] w-16 h-16 bg-skyblue/10 rounded-full blur-xl animate-pulse" />
        <div className="absolute bottom-[20%] right-[5%] w-24 h-24 bg-coralpink/15 rounded-full blur-xl animate-pulse" />
        
        <div className="flex flex-col items-center text-center gap-6 z-10 w-full max-w-md">
          {/* Use mascot */}
          <div className="kid-float">
            <Mascot state="wave" lang={currentLanguage} message="Preparing your learning adventure..." />
          </div>

          {/* Slogan */}
          <div className="space-y-2 mt-4">
            <p className="text-lg font-black text-navy leading-tight">
              {currentLanguage === 'ur' ? 'آپ کی پڑھائی کا سفر شروع ہو رہا ہے...' : currentLanguage === 'ar' ? 'جاري تحضير مغامرة التعلم الخاصة بك...' : 'Preparing your learning adventure...'}
            </p>
            <p className="text-xs font-bold text-slate-700 uppercase tracking-widest">
              Please wait • برائے مہربانی انتظار کریں • يرجى الانتظار
            </p>
          </div>

          {/* Circular progress loader */}
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mt-2"></div>
        </div>
      </div>
    );
  }

  return (
    <div 
      id="app-root-viewport" 
      dir={isRtl ? 'rtl' : 'ltr'}
      className="min-h-screen bg-cream flex flex-col justify-between"
    >
      {activeScreen !== 'splash' && (
        <div id="global-app-header" className="w-full bg-[#FFFBEB] border-b border-amber-200 py-2.5 px-4 md:px-8 flex flex-wrap gap-3 justify-between items-center z-50 select-none">
          {/* Left Side: Playground simulation */}
          <div className="flex items-center gap-1.5 flex-wrap text-xs font-black text-slate-800">
            <span className={currentLanguage === 'ur' ? 'font-urdu' : currentLanguage === 'ar' ? 'font-arabic' : ''}>
              {t('playgroundLabel')}
            </span>
            <button
              onClick={simulateActiveMinute}
              className={`bg-white hover:bg-slate-50 px-3 py-1 rounded-full text-[10px] font-black border border-amber-300 text-amber-600 shadow-sm active:translate-y-[1px] cursor-pointer ${
                currentLanguage === 'ur' ? 'font-urdu' : currentLanguage === 'ar' ? 'font-arabic' : ''
              }`}
            >
              {t('addStudyMinBtn')}
            </button>
            {activeProfile && (
              <span className={`bg-white/80 px-2 py-0.5 rounded-full border border-amber-200 text-[10px] text-slate-600 ${
                currentLanguage === 'ur' ? 'font-urdu' : currentLanguage === 'ar' ? 'font-arabic' : ''
              }`}>
                ⏰ {activeProfile.todayMinutes} / {activeProfile.dailyGoalMinutes}{t('studyMinSuffix')}
              </span>
            )}
          </div>

          {/* Right Side: Global Language Indicator & Change Button */}
          <div className="flex items-center gap-2">
            {/* Active profile name pill */}
            {activeProfile && (
              <div className="hidden sm:flex items-center gap-1.5 bg-white/80 pl-1.5 pr-3 py-1 rounded-full border border-amber-200 text-xs font-black text-slate-700">
                {activeProfile.photo ? (
                  <img
                    src={activeProfile.photo}
                    alt={activeProfile.name}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="w-5.5 h-5.5 rounded-full object-cover border border-amber-300"
                  />
                ) : (
                  <span>{activeProfile.avatar}</span>
                )}
                <span>{activeProfile.name}</span>
              </div>
            )}

            {/* Connection Indicator Pill */}
            <button
              onClick={() => setShowConnectionInfo(true)}
              title={isOnline ? t('statusOnline') : t('statusOffline')}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full border-2 text-xs font-black shadow-sm transition-all cursor-pointer active:translate-y-[1px] select-none ${
                isOnline 
                  ? 'bg-emerald-50 hover:bg-emerald-100 border-emerald-300 text-emerald-800' 
                  : 'bg-rose-50 hover:bg-rose-100 border-rose-300 text-rose-800 animate-pulse'
              }`}
            >
              {isOnline ? (
                <Wifi className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" />
              ) : (
                <WifiOff className="w-3.5 h-3.5 text-rose-500 stroke-[3]" />
              )}
              <span className={currentLanguage === 'ur' ? 'font-urdu text-[11px]' : currentLanguage === 'ar' ? 'font-arabic text-[11px]' : ''}>
                {isOnline ? t('statusOnline') : t('statusOffline')}
              </span>
            </button>

            {/* Language Pill */}
            <div className="bg-white rounded-full border-2 border-amber-300 px-3 py-1 flex items-center gap-2 shadow-sm text-xs font-black">
              <span className="text-sm">🌐</span>
              <span className="text-amber-800">
                {currentLanguage === 'en' ? 'EN' : currentLanguage === 'ur' ? 'اردو' : 'العربية'}
              </span>
              <button
                onClick={() => setShowLanguageModal(true)}
                className="bg-[#FF6B8B] hover:bg-[#FF4F73] text-white px-2.5 py-0.5 rounded-full text-[10px] font-black transition-all cursor-pointer active:translate-y-[1px]"
              >
                {t('changeLanguageBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Screen Display */}
      <main className="flex-grow flex items-center justify-center w-full">
        <ErrorBoundary onReset={() => handleNavigate('dashboard')}>
          <React.Suspense fallback={
            <div className="flex flex-col items-center justify-center w-full min-h-[450px] bg-cream rounded-[2rem] p-8">
              <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 font-black text-navy text-sm">Loading Fun...</p>
            </div>
          }>
            <AnimatePresence mode="wait">
          {activeScreen === 'splash' && (
            <motion.div
              key="splash"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full"
            >
              <SplashAndProfile
                currentLanguage={currentLanguage}
                setLanguage={setLanguage}
                profiles={profiles}
                onSelectProfile={(p) => {
                  setActiveProfileId(p.id);
                  localStorage.setItem(LOCAL_ACTIVE_ID_KEY, p.id);
                  handleNavigate('dashboard');
                }}
                onCreateProfile={handleCreateProfile}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'dashboard' && activeProfile && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="w-full"
            >
              <Dashboard
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onNavigate={handleNavigate}
                soundEnabled={soundEnabled}
                onSimulateMinute={simulateActiveMinute}
                onPrefetchScreen={handlePrefetchScreen}
              />
            </motion.div>
          )}

          {activeScreen.startsWith('alphabets') && activeProfile && (
            <motion.div
              key="alphabets"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <AlphabetModule
                lang={currentLanguage}
                alphabetType={
                  activeScreen === 'alphabets-ur' ? 'ur' :
                  activeScreen === 'alphabets-ar' ? 'ar' : 'en'
                }
                activeProfile={activeProfile}
                onAwardStars={handleAwardAlphabetStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'words' && activeProfile && (
            <motion.div
              key="words"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <WordsModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardWordStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'numbers' && activeProfile && (
            <motion.div
              key="numbers"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <NumbersModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardNumberStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
                initialMode="learn"
              />
            </motion.div>
          )}

          {activeScreen === 'number-speaker' && activeProfile && (
            <motion.div
              key="numbers-speaker"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <NumbersModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardNumberStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
                initialMode="speaker"
              />
            </motion.div>
          )}

          {activeScreen === 'activities' && activeProfile && (
            <motion.div
              key="activities"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <ActivitiesModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardGameStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'rewards' && activeProfile && (
            <motion.div
              key="rewards"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <RewardsModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onClaimBadge={handleClaimBadge}
                onClaimSticker={handleClaimSticker}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'voice-practice' && activeProfile && (
            <motion.div
              key="voice-practice"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <VoicePracticeModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardVoiceStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'weather' && activeProfile && (
            <motion.div
              key="weather"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <WeatherModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardWeatherStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'months' && activeProfile && (
            <motion.div
              key="months"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <MonthsModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardMonthsStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'days' && activeProfile && (
            <motion.div
              key="days"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <DaysModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardDaysStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'body-parts' && activeProfile && (
            <motion.div
              key="body-parts"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <BodyPartsModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardBodyPartsStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'islamic-months' && activeProfile && (
            <motion.div
              key="islamic-months"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <IslamicMonthsModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardIslamicMonthsStars}
                onGoBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'calendar-learning' && activeProfile && (
            <motion.div
              key="calendar-learning"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <CalendarLearningModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardCalendarLearningStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'ramadan-mode' && activeProfile && (
            <motion.div
              key="ramadan-mode"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <RamadanModeModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardRamadanModeStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'write-learn' && activeProfile && (
            <motion.div
              key="write-learn"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <WriteLearnModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardWriteLearnStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'basic-communication' && activeProfile && (
            <motion.div
              key="basic-communication"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <BasicCommunicationModule
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onAwardStars={handleAwardCommunicationStars}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'quiz' && activeProfile && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <QuizModule
                lang={currentLanguage}
                activeProfile={activeProfile}
                onQuizComplete={handleQuizComplete}
                onGoBack={() => handleNavigate('dashboard')}
                soundEnabled={soundEnabled}
              />
            </motion.div>
          )}

          {activeScreen === 'parent' && activeProfile && (
            <motion.div
              key="parent"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full lazy-module"
            >
              <ParentArea
                currentLanguage={currentLanguage}
                setLanguage={setLanguage}
                activeProfile={activeProfile}
                profiles={profiles}
                soundEnabled={soundEnabled}
                setSoundEnabled={handleToggleSound}
                onResetProgress={handleResetProgress}
                onUpdateDailyGoal={handleUpdateDailyGoal}
                onSwitchProfile={handleSwitchProfile}
                syncStatus={syncStatus}
                lastSaved={lastSaved}
                onImportProfiles={handleImportProfiles}
                onUpdateAvatarCustom={handleUpdateAvatarCustom}
                onUpdateProfileInfo={handleUpdateProfileInfo}
              />
            </motion.div>
          )}

          {/* Safety Fallback to prevent white screen for unknown or corrupt activeScreen state */}
          {!['splash', 'dashboard', 'words', 'numbers', 'number-speaker', 'activities', 'rewards', 'voice-practice', 'weather', 'months', 'days', 'body-parts', 'islamic-months', 'calendar-learning', 'ramadan-mode', 'write-learn', 'basic-communication', 'quiz', 'parent'].includes(activeScreen) && !activeScreen.startsWith('alphabets') && (
            <motion.div
              key="fallback-dashboard"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full"
            >
              <Dashboard
                currentLanguage={currentLanguage}
                activeProfile={activeProfile}
                onNavigate={handleNavigate}
                soundEnabled={soundEnabled}
                onSimulateMinute={simulateActiveMinute}
                onPrefetchScreen={handlePrefetchScreen}
              />
            </motion.div>
          )}
        </AnimatePresence>
        </React.Suspense>
        </ErrorBoundary>
      </main>

      {(activeScreen === 'splash' || activeScreen === 'dashboard') && (
        <React.Suspense fallback={null}>
          <SeoContent />
        </React.Suspense>
      )}

      {/* Floating Kids Bottom Tab bar (for non-splash screens) */}
      {activeScreen !== 'splash' && activeProfile && (
        <div id="floating-tab-bar" className="sticky bottom-2 sm:bottom-4 z-40 w-full px-2 sm:px-4 md:px-8 pointer-events-none mt-auto">
          <div className="max-w-xl mx-auto bg-white rounded-[2rem] sm:rounded-[2.5rem] md:rounded-[3rem] p-1.5 sm:p-2 md:p-3 flex justify-between items-center shadow-[0_6px_0_#DED6C1] sm:shadow-[0_8px_0_#DED6C1] px-2.5 sm:px-5 md:px-10 border-2 sm:border-4 border-white pointer-events-auto gap-1">
            {/* Home Tab */}
            <button
              onClick={() => handleNavigate('dashboard')}
              className={`flex flex-col items-center gap-0.5 sm:gap-1 cursor-pointer transition-all p-1 sm:p-1.5 min-w-[44px] min-h-[44px] justify-center touch-manipulation ${
                activeScreen === 'dashboard' ? 'scale-105 md:scale-110 opacity-100' : 'opacity-65 hover:opacity-100 active:scale-95'
              }`}
            >
              <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-14 md:h-14 bg-[#FFD966] rounded-full flex items-center justify-center shadow-[0_3px_0_#F2C029] sm:shadow-[0_4px_0_#F2C029] text-white">
                <Home className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 stroke-[3]" />
              </div>
              <span className={`text-[9px] sm:text-[10px] md:text-xs font-black uppercase tracking-wider text-[#263238] whitespace-nowrap ${
                currentLanguage === 'ur' ? 'font-urdu text-[11px] sm:text-xs md:text-sm' : currentLanguage === 'ar' ? 'font-arabic text-[10px] sm:text-[11px] md:text-xs' : ''
              }`}>
                {t('navHome')}
              </span>
            </button>

            {/* Learn Alphabets Tab */}
            <button
              onClick={() => {
                const screen = currentLanguage === 'ur' ? 'alphabets-ur' : currentLanguage === 'ar' ? 'alphabets-ar' : 'alphabets-en';
                handleNavigate(screen);
              }}
              className={`flex flex-col items-center gap-0.5 sm:gap-1 cursor-pointer transition-all p-1 sm:p-1.5 min-w-[44px] min-h-[44px] justify-center touch-manipulation ${
                activeScreen.startsWith('alphabets') ? 'scale-105 md:scale-110 opacity-100' : 'opacity-65 hover:opacity-100 active:scale-95'
              }`}
            >
              <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-14 md:h-14 bg-[#4FC3F7] rounded-full flex items-center justify-center shadow-[0_3px_0_#2196F3] sm:shadow-[0_4px_0_#2196F3] text-white">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 stroke-[3]" />
              </div>
              <span className={`text-[9px] sm:text-[10px] md:text-xs font-black uppercase tracking-wider text-[#263238] whitespace-nowrap ${
                currentLanguage === 'ur' ? 'font-urdu text-[11px] sm:text-xs md:text-sm' : currentLanguage === 'ar' ? 'font-arabic text-[10px] sm:text-[11px] md:text-xs' : ''
              }`}>
                {t('navLearn')}
              </span>
            </button>

            {/* Play Games Tab */}
            <button
              onClick={() => handleNavigate('activities')}
              className={`flex flex-col items-center gap-0.5 sm:gap-1 cursor-pointer transition-all p-1 sm:p-1.5 min-w-[44px] min-h-[44px] justify-center touch-manipulation ${
                activeScreen === 'activities' ? 'scale-105 md:scale-110 opacity-100' : 'opacity-65 hover:opacity-100 active:scale-95'
              }`}
            >
              <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-14 md:h-14 bg-[#7EDCBF] rounded-full flex items-center justify-center shadow-[0_3px_0_#57B79B] sm:shadow-[0_4px_0_#57B79B] text-white">
                <Gamepad2 className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 stroke-[3]" />
              </div>
              <span className={`text-[9px] sm:text-[10px] md:text-xs font-black uppercase tracking-wider text-[#263238] whitespace-nowrap ${
                currentLanguage === 'ur' ? 'font-urdu text-[11px] sm:text-xs md:text-sm' : currentLanguage === 'ar' ? 'font-arabic text-[10px] sm:text-[11px] md:text-xs' : ''
              }`}>
                {t('navPlay')}
              </span>
            </button>

            {/* Rewards Tab */}
            <button
              onClick={() => handleNavigate('rewards')}
              className={`flex flex-col items-center gap-0.5 sm:gap-1 cursor-pointer transition-all p-1 sm:p-1.5 min-w-[44px] min-h-[44px] justify-center touch-manipulation ${
                activeScreen === 'rewards' ? 'scale-105 md:scale-110 opacity-100' : 'opacity-65 hover:opacity-100 active:scale-95'
              }`}
            >
              <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-14 md:h-14 bg-[#B9A7F7] rounded-full flex items-center justify-center shadow-[0_3px_0_#9E8EDC] sm:shadow-[0_4px_0_#9E8EDC] text-white">
                <Award className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 stroke-[3]" />
              </div>
              <span className={`text-[9px] sm:text-[10px] md:text-xs font-black uppercase tracking-wider text-[#263238] whitespace-nowrap ${
                currentLanguage === 'ur' ? 'font-urdu text-[11px] sm:text-xs md:text-sm' : currentLanguage === 'ar' ? 'font-arabic text-[10px] sm:text-[11px] md:text-xs' : ''
              }`}>
                {t('navPrizes')}
              </span>
            </button>

            <div className="w-px bg-[#DED6C1] h-6 sm:h-8 md:h-10 mx-0.5 sm:mx-1 md:mx-2" />

            {/* Parents Tab with lock sign */}
            <button
              onClick={() => handleNavigate('parent')}
              className={`flex flex-col items-center gap-0.5 sm:gap-1 cursor-pointer transition-all p-1 sm:p-1.5 min-w-[44px] min-h-[44px] justify-center touch-manipulation ${
                activeScreen === 'parent' ? 'scale-105 md:scale-110 opacity-100' : 'opacity-65 hover:opacity-100 active:scale-95'
              }`}
            >
              <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-14 md:h-14 bg-white border-2 sm:border-4 border-[#263238] rounded-full flex items-center justify-center shadow-[0_3px_0_#263238] sm:shadow-[0_4px_0_#263238] text-[#263238]">
                <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 stroke-[3]" />
              </div>
              <span className={`text-[9px] sm:text-[10px] md:text-xs font-black uppercase tracking-wider text-[#263238] whitespace-nowrap ${
                currentLanguage === 'ur' ? 'font-urdu text-[11px] sm:text-xs md:text-sm' : currentLanguage === 'ar' ? 'font-arabic text-[10px] sm:text-[11px] md:text-xs' : ''
              }`}>
                {t('navParent')}
              </span>
            </button>
          </div>
        </div>
      )}
      <AnimatePresence>
        {showLanguageModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLanguageModal(false)}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            {/* Modal Card */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-[2.5rem] p-6 md:p-8 max-w-md w-full border-4 border-amber-300 shadow-2xl relative z-10 text-center select-none"
            >
              <div className="text-5xl mb-3 animate-bounce">🌐</div>
              <h3 className="text-2xl font-black text-navy mb-2">
                {t('modalTitle')}
              </h3>
              <p className="text-sm font-bold text-slate-700 mb-6 px-2">
                {t('modalWarning')}
              </p>

              {/* Language Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleGlobalLanguageSelect('en')}
                  className={`py-3.5 px-6 rounded-2xl font-black text-lg transition-all border-3 flex items-center justify-between cursor-pointer ${
                    currentLanguage === 'en'
                      ? 'bg-skyblue text-navy border-blue-400 shadow-md'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span>🇺🇸 English</span>
                  {currentLanguage === 'en' && <span className="text-xl">⭐</span>}
                </button>
                <button
                  onClick={() => handleGlobalLanguageSelect('ur')}
                  className={`py-3.5 px-6 rounded-2xl font-black text-lg font-urdu transition-all border-3 flex items-center justify-between cursor-pointer leading-none ${
                    currentLanguage === 'ur'
                      ? 'bg-orangeaccent text-navy border-orange-400 shadow-md'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span>اردو</span>
                  {currentLanguage === 'ur' && <span className="text-xl">⭐</span>}
                </button>
                <button
                  onClick={() => handleGlobalLanguageSelect('ar')}
                  className={`py-3.5 px-6 rounded-2xl font-black text-lg font-arabic transition-all border-3 flex items-center justify-between cursor-pointer ${
                    currentLanguage === 'ar'
                      ? 'bg-mintgreen text-navy border-emerald-400 shadow-md'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span>العربية</span>
                  {currentLanguage === 'ar' && <span className="text-xl">⭐</span>}
                </button>
              </div>

              {/* Cancel Button */}
              <button
                onClick={() => setShowLanguageModal(false)}
                className="mt-6 text-xs font-bold text-slate-600 hover:text-slate-800 block mx-auto underline cursor-pointer"
              >
                {t('closeBtn')}
              </button>
            </motion.div>
          </div>
        )}

        {showConnectionInfo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConnectionInfo(false)}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            {/* Modal Card */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-[2.5rem] p-6 md:p-8 max-w-md w-full border-4 border-amber-300 shadow-2xl relative z-10 text-center select-none"
            >
              <div className="text-5xl mb-3 animate-bounce">
                {isOnline ? '⚡🌐' : '💾🚀'}
              </div>
              <h3 className="text-2xl font-black text-slate-800 mb-2">
                {isOnline ? t('statusOnline') : t('statusOffline')}
              </h3>
              <p className="text-sm font-bold text-slate-600 mb-4 px-2 leading-relaxed">
                {isOnline ? t('storageDescOnline') : t('storageDescOffline')}
              </p>
              
              <div className="bg-amber-50 rounded-2xl p-4 border-2 border-amber-100 text-left text-xs font-bold text-slate-700 space-y-2 mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-base">🚀</span>
                  <span><strong>Offline Support:</strong> Works completely offline!</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base">💾</span>
                  <span><strong>Local Storage:</strong> Progress is saved on this browser/device.</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base">🔒</span>
                  <span><strong>Privacy Friendly:</strong> No personal learning data leaves your device.</span>
                </div>
              </div>

              <button
                onClick={() => setShowConnectionInfo(false)}
                className="bg-[#FF6B8B] hover:bg-[#FF4F73] text-white px-6 py-2 rounded-full text-sm font-black transition-all cursor-pointer shadow-md hover:shadow-lg active:translate-y-[1px]"
              >
                {t('closeBtn')}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
